import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MONTHS_LONG, toDate } from '../../utils/dates';
import MonthYearSelect from './MonthYearSelect';
import {
    WEEKDAYS,
    addDays,
    addMonths,
    buildMonthGrid,
    ceilingAfter,
    clampToRange,
    dayValue,
    isDayDisabled,
    isSameDay,
    isWithinInterval,
    startOfDay,
    startOfMonth,
} from './calendarUtils';

const MonthGrid = ({
    monthDate,
    focusedDate,
    describeDay,
    onSelect,
    onHover,
}) => {
    const weeks = useMemo(
        () => buildMonthGrid(monthDate.getFullYear(), monthDate.getMonth()),
        [monthDate]
    );

    return (
        <div className="dt-cal__grid" role="grid" aria-label={`${MONTHS_LONG[monthDate.getMonth()]} ${monthDate.getFullYear()}`}>
            <div className="dt-cal__weekdays" role="row">
                {WEEKDAYS.map(day => (
                    <span key={day} className="dt-cal__weekday" role="columnheader">{day}</span>
                ))}
            </div>
            {weeks.map((week, weekIndex) => (
                <div className="dt-cal__week" role="row" key={weekIndex}>
                    {week.map((day) => {
                        const state = describeDay(day, monthDate);
                        const focused = isSameDay(day, focusedDate);

                        return (
                            <button
                                key={day.getTime()}
                                type="button"
                                role="gridcell"
                                tabIndex={focused ? 0 : -1}
                                data-focused={focused || undefined}
                                disabled={state.disabled}
                                aria-selected={state.selected || state.rangeEdge}
                                aria-current={state.today ? 'date' : undefined}
                                className={[
                                    'dt-cal__day',
                                    state.outside ? 'is-outside' : '',
                                    state.today ? 'is-today' : '',
                                    state.selected || state.rangeEdge ? 'is-selected' : '',
                                    state.inRange ? 'is-in-range' : '',
                                    state.rangeStart ? 'is-range-start' : '',
                                    state.rangeEnd ? 'is-range-end' : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => onSelect(day)}
                                onMouseEnter={() => onHover(day)}
                            >
                                {day.getDate()}
                            </button>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

const Calendar = ({
    mode = 'single',
    value,
    onSelect,
    minDate,
    maxDate,
    excludeIntervals = [],
    monthsShown = 1,
    titleId,
}) => {
    const single = mode === 'single';
    const selected = single ? toDate(value) : null;
    const from = single ? null : toDate(value?.from);
    const to = single ? null : toDate(value?.to);

    const [rangeStart, setRangeStart] = useState(null);
    const [hoverDate, setHoverDate] = useState(null);
    const [pickingPeriod, setPickingPeriod] = useState(false);
    const [shouldFocusDay, setShouldFocusDay] = useState(false);

    const initialView = useMemo(
        () => startOfMonth(selected || from || clampToRange(new Date(), minDate, maxDate)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );
    const [viewDate, setViewDate] = useState(initialView);
    const [focusedDate, setFocusedDate] = useState(
        () => selected || from || clampToRange(new Date(), minDate, maxDate) || new Date()
    );

    const gridRef = useRef(null);

    // While picking a range end, an excluded interval after the start is a hard
    // ceiling — the range must not span over an already-used period.
    const effectiveMax = useMemo(() => {
        if (single || !rangeStart) return maxDate;
        const ceiling = ceilingAfter(rangeStart, excludeIntervals);
        if (!ceiling) return maxDate;
        const max = toDate(maxDate);
        return max && dayValue(max) < dayValue(ceiling) ? max : ceiling;
    }, [single, rangeStart, excludeIntervals, maxDate]);

    const disabledOptions = useMemo(
        () => ({ minDate: rangeStart || minDate, maxDate: effectiveMax, excludeIntervals }),
        [rangeStart, minDate, effectiveMax, excludeIntervals]
    );

    const isDisabled = useCallback(
        (day) => isDayDisabled(day, disabledOptions),
        [disabledOptions]
    );

    const months = useMemo(
        () => Array.from({ length: monthsShown }, (_, index) => addMonths(viewDate, index)),
        [viewDate, monthsShown]
    );

    // Browsing months (arrows, month/year selector). The roving tabstop follows
    // the view so the grid stays keyboard-reachable, but DOM focus is left alone.
    const goToMonth = useCallback((next) => {
        const start = startOfMonth(next);
        setViewDate(start);
        setFocusedDate((current) => {
            const last = addMonths(start, monthsShown - 1);
            const currentMonth = startOfMonth(current);
            const alreadyVisible = dayValue(currentMonth) >= dayValue(start)
                && dayValue(currentMonth) <= dayValue(last);
            if (alreadyVisible) return current;

            // Keep the day-of-month where the target month is long enough for it.
            const candidate = new Date(start.getFullYear(), start.getMonth(), current.getDate());
            return candidate.getMonth() === start.getMonth() ? candidate : start;
        });
    }, [monthsShown]);

    // Keyboard navigation moves the focused day and pulls the view along with it.
    // Deliberately not an effect on focusedDate: that also fired when the user
    // browsed months by mouse, snapping the view back to the selected day.
    const moveFocus = useCallback((next) => {
        setFocusedDate(next);
        setShouldFocusDay(true);
        setViewDate((current) => {
            const nextMonth = startOfMonth(next);
            if (dayValue(nextMonth) < dayValue(current)) return nextMonth;
            if (dayValue(nextMonth) > dayValue(addMonths(current, monthsShown - 1))) {
                return addMonths(nextMonth, -(monthsShown - 1));
            }
            return current;
        });
    }, [monthsShown]);

    // Only pull focus after keyboard navigation, never on open — otherwise the
    // panel steals focus from the trigger input as soon as it mounts.
    useEffect(() => {
        if (!shouldFocusDay) return;
        gridRef.current?.querySelector('[data-focused="true"]')?.focus();
        setShouldFocusDay(false);
    }, [shouldFocusDay, focusedDate]);

    const commitRange = useCallback((day) => {
        if (!rangeStart) {
            setRangeStart(day);
            setFocusedDate(day);
            onSelect({ from: startOfDay(day), to: null }, false);
            return;
        }

        const [start, end] = dayValue(day) < dayValue(rangeStart) ? [day, rangeStart] : [rangeStart, day];
        const spansExcluded = excludeIntervals.some(({ start: blockStart, end: blockEnd }) =>
            isWithinInterval(blockStart, { start, end }) || isWithinInterval(blockEnd, { start, end })
        );

        if (spansExcluded) {
            // The swapped range would swallow a used period — restart instead.
            setRangeStart(day);
            setFocusedDate(day);
            onSelect({ from: startOfDay(day), to: null }, false);
            return;
        }

        setRangeStart(null);
        setHoverDate(null);
        onSelect({ from: startOfDay(start), to: startOfDay(end) }, true);
    }, [rangeStart, excludeIntervals, onSelect]);

    const handleSelect = useCallback((day) => {
        if (isDisabled(day)) return;
        setFocusedDate(day);
        if (single) {
            onSelect(startOfDay(day), true);
            return;
        }
        commitRange(day);
    }, [isDisabled, single, onSelect, commitRange]);

    const describeDay = useCallback((day, monthDate) => {
        const previewEnd = rangeStart ? (hoverDate || rangeStart) : null;
        const activeFrom = rangeStart || from;
        const activeTo = rangeStart ? previewEnd : to;

        const inRange = !single && activeFrom && activeTo
            && isWithinInterval(day, { start: activeFrom, end: activeTo });

        return {
            outside: day.getMonth() !== monthDate.getMonth(),
            today: isSameDay(day, new Date()),
            disabled: isDisabled(day),
            selected: single && isSameDay(day, selected),
            rangeEdge: !single && (isSameDay(day, activeFrom) || isSameDay(day, activeTo)),
            rangeStart: !single && isSameDay(day, activeFrom),
            rangeEnd: !single && isSameDay(day, activeTo),
            inRange,
        };
    }, [single, selected, from, to, rangeStart, hoverDate, isDisabled]);

    const handleKeyDown = (event) => {
        const moves = {
            ArrowLeft: () => addDays(focusedDate, -1),
            ArrowRight: () => addDays(focusedDate, 1),
            ArrowUp: () => addDays(focusedDate, -7),
            ArrowDown: () => addDays(focusedDate, 7),
            PageUp: () => addMonths(focusedDate, -1),
            PageDown: () => addMonths(focusedDate, 1),
            Home: () => addDays(focusedDate, -((focusedDate.getDay() + 6) % 7)),
            End: () => addDays(focusedDate, 6 - ((focusedDate.getDay() + 6) % 7)),
        };

        // Enter/Space are deliberately not handled here — the day cells are real
        // buttons and activate natively; intercepting would select twice.
        if (!moves[event.key]) return;

        event.preventDefault();
        moveFocus(clampToRange(moves[event.key](), minDate, effectiveMax));
    };

    const headingMonth = months[0];

    return (
        <div className="dt-cal">
            <div className="dt-cal__header">
                <button
                    type="button"
                    className="dt-cal__nav"
                    aria-label="Previous month"
                    onClick={() => goToMonth(addMonths(viewDate, -1))}
                >
                    <span className="fas fa-chevron-left" aria-hidden="true" />
                </button>

                <button
                    type="button"
                    id={titleId}
                    className="dt-cal__title"
                    aria-expanded={pickingPeriod}
                    onClick={() => setPickingPeriod(open => !open)}
                >
                    {monthsShown > 1
                        ? `${MONTHS_LONG[headingMonth.getMonth()]} — ${MONTHS_LONG[months[months.length - 1].getMonth()]} ${months[months.length - 1].getFullYear()}`
                        : `${MONTHS_LONG[headingMonth.getMonth()]} ${headingMonth.getFullYear()}`}
                    <span className="fas fa-chevron-down dt-cal__title-caret" aria-hidden="true" />
                </button>

                <button
                    type="button"
                    className="dt-cal__nav"
                    aria-label="Next month"
                    onClick={() => goToMonth(addMonths(viewDate, 1))}
                >
                    <span className="fas fa-chevron-right" aria-hidden="true" />
                </button>
            </div>

            <div
                className="dt-cal__body"
                ref={gridRef}
                onKeyDown={handleKeyDown}
                onMouseLeave={() => setHoverDate(null)}
            >
                {pickingPeriod ? (
                    <MonthYearSelect
                        viewDate={viewDate}
                        minDate={minDate}
                        maxDate={maxDate}
                        onSelect={(next) => {
                            goToMonth(next);
                            setPickingPeriod(false);
                        }}
                    />
                ) : (
                    <div className="dt-cal__months">
                        {months.map(monthDate => (
                            <div className="dt-cal__month" key={monthDate.getTime()}>
                                {monthsShown > 1 && (
                                    <div className="dt-cal__month-label">
                                        {MONTHS_LONG[monthDate.getMonth()]} {monthDate.getFullYear()}
                                    </div>
                                )}
                                <MonthGrid
                                    monthDate={monthDate}
                                    focusedDate={focusedDate}
                                    describeDay={describeDay}
                                    onSelect={handleSelect}
                                    onHover={setHoverDate}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="dt-cal__live" aria-live="polite">
                {`${MONTHS_LONG[headingMonth.getMonth()]} ${headingMonth.getFullYear()}`}
            </div>
        </div>
    );
};

export default Calendar;
